import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Shield, Star, Users, Calendar, MessageSquare, Bell, 
  CheckCircle, Lock, CreditCard, Gift
} from "lucide-react";

const MembershipBenefitsSection = () => {
  const playerFeatures = [
    "Unlimited Event Access",
    "Advanced Search Tools",
    "Player Profiles",
    "Event History",
    "Review System",
    "Mobile Access",
    "Calendar Integration",
    "Notification Preferences",
  ];

  const hostFeatures = [
    "Free Event Creation",
    "Professional Templates",
    "Guest Management",
    "Payment Processing",
    "Marketing Exposure",
    "Analytics Dashboard",
    "Host Ratings",
    "Venue Access",
  ];

  const tiers = [
    {
      name: "FREE TRIAL",
      price: "0",
      period: "7 days",
      features: ["Browse events", "Limited event access", "Basic search"],
      highlight: false,
    },
    {
      name: "MONTHLY",
      price: "19.99",
      period: "per month",
      features: ["Unlimited events", "Host games", "Advanced search", "Priority registration", "Chat access"],
      highlight: true,
    },
    {
      name: "ANNUAL",
      price: "149.99",
      period: "per year",
      features: ["All Monthly features", "Venue discounts", "Championship access", "Exclusive events", "B2B™ merchandise"],
      highlight: false,
      badge: "BEST VALUE",
    },
  ];

  return (
    <div data-testid="membership-section">
      {/* Section Header */}
      <div className="mb-12">
        <h2 className="font-bebas text-4xl md:text-5xl tracking-wider text-white">
          JOIN THE BOARD2BOSTON™ COMMUNITY
        </h2>
        <p className="mt-4 text-[#A1A1AA] font-ubuntu text-lg max-w-3xl">
          Board2Boston™ membership unlocks access to Boston's premier gaming community. 
          Our verification process ensures a safe, quality experience while our platform provides 
          tools that make gaming more accessible and enjoyable than ever.
        </p>
      </div>

      {/* Verified Community */}
      <div className="mb-16 p-8 bg-[#0A0A0A] rounded-lg border border-[#7a6200]/30">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-8 h-8 text-[#7a6200]" />
          <h3 className="font-bebas text-2xl tracking-wider text-white">VERIFIED COMMUNITY</h3>
        </div>
        <p className="text-[#A1A1AA] font-ubuntu mb-6">
          Every Board2Boston™ member completes our comprehensive verification process.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-[#7a6200] mt-0.5" />
            <div>
              <p className="text-white font-ubuntu font-medium">Identity Verification</p>
              <p className="text-[#52525B] font-ubuntu text-sm">Government-issued ID confirmation</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-[#7a6200] mt-0.5" />
            <div>
              <p className="text-white font-ubuntu font-medium">Age Verification</p>
              <p className="text-[#52525B] font-ubuntu text-sm">Compliance with venue requirements</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-[#7a6200] mt-0.5" />
            <div>
              <p className="text-white font-ubuntu font-medium">Background Security</p>
              <p className="text-[#52525B] font-ubuntu text-sm">Enhanced safety for all members</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-[#7a6200] mt-0.5" />
            <div>
              <p className="text-white font-ubuntu font-medium">Trusted Network</p>
              <p className="text-[#52525B] font-ubuntu text-sm">Play with confidence</p>
            </div>
          </div>
        </div>
      </div>

      {/* Membership Features */}
      <div className="mb-16">
        <h3 className="font-bebas text-2xl tracking-wider text-[#7a6200] mb-8">MEMBERSHIP FEATURES</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* For Players */}
          <Card className="bg-[#0A0A0A] border border-white/5">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <Users className="w-6 h-6 text-[#7a6200]" />
                <h4 className="font-bebas text-xl tracking-wider text-white">FOR PLAYERS</h4>
              </div>
              <ul className="space-y-3">
                {playerFeatures.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-[#7a6200]" />
                    <span className="text-[#A1A1AA] font-ubuntu">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* For Hosts */}
          <Card className="bg-[#0A0A0A] border border-white/5">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <Star className="w-6 h-6 text-[#7a6200]" />
                <h4 className="font-bebas text-xl tracking-wider text-white">FOR HOSTS</h4>
              </div>
              <ul className="space-y-3">
                {hostFeatures.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-[#7a6200]" />
                    <span className="text-[#A1A1AA] font-ubuntu">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Pricing Tiers */}
      <div className="mb-16">
        <h3 className="font-bebas text-2xl tracking-wider text-[#7a6200] mb-8">MEMBERSHIP INVESTMENT</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((tier, i) => (
            <Card 
              key={i}
              className={`bg-[#0A0A0A] border relative overflow-hidden ${
                tier.highlight ? "border-[#7a6200]" : "border-white/5"
              }`}
              data-testid={`tier-${tier.name.toLowerCase().replace(' ', '-')}`}
            >
              {tier.badge && (
                <Badge className="absolute top-4 right-4 bg-[#7a6200] text-white border-0">
                  {tier.badge}
                </Badge>
              )}
              <CardContent className="p-6">
                <h4 className="font-bebas text-2xl tracking-wider text-white mb-2">{tier.name}</h4>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="font-bebas text-5xl text-[#7a6200]">${tier.price}</span>
                </div>
                <p className="text-[#52525B] font-ubuntu text-sm mb-6">{tier.period}</p>
                
                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-[#7a6200]" />
                      <span className="text-[#A1A1AA] font-ubuntu text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button 
                  className={tier.highlight ? "btn-gold w-full" : "btn-outline-gold w-full"}
                  data-testid={`select-tier-${i}`}
                >
                  {tier.price === "0" ? "START FREE" : "SELECT PLAN"}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="text-center text-[#52525B] font-ubuntu text-sm mt-6">
          No hidden fees • Cancel anytime • Transparent pricing
        </p>
      </div>

      {/* Getting Started */}
      <div className="mb-16 p-8 bg-[#0A0A0A] rounded-lg border border-white/5">
        <h3 className="font-bebas text-2xl tracking-wider text-[#7a6200] mb-6">
          GETTING STARTED
        </h3>
        <p className="text-[#A1A1AA] font-ubuntu mb-8">
          Join Board2Boston™ in three simple steps.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="flex items-start gap-4">
            <span className="font-bebas text-4xl text-[#7a6200]/30">01</span>
            <div>
              <p className="text-white font-ubuntu font-medium">Register</p>
              <p className="text-[#52525B] font-ubuntu text-sm">Create your account with basic information</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <span className="font-bebas text-4xl text-[#7a6200]/30">02</span>
            <div>
              <p className="text-white font-ubuntu font-medium">Verify</p>
              <p className="text-[#52525B] font-ubuntu text-sm">Complete our secure ID verification</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <span className="font-bebas text-4xl text-[#7a6200]/30">03</span>
            <div>
              <p className="text-white font-ubuntu font-medium">Play</p>
              <p className="text-[#52525B] font-ubuntu text-sm">Start browsing and attending events</p>
            </div>
          </div>
        </div>
      </div>

      {/* Safety & Privacy */}
      <div className="p-8 bg-[#0A0A0A] rounded-lg border border-white/5">
        <div className="flex items-center gap-3 mb-6">
          <Lock className="w-6 h-6 text-[#7a6200]" />
          <h3 className="font-bebas text-2xl tracking-wider text-white">SAFETY AND PRIVACY</h3>
        </div>
        <p className="text-[#A1A1AA] font-ubuntu mb-6">
          Your security and privacy are our top priorities.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-[#7a6200] mt-0.5" />
            <div>
              <p className="text-white font-ubuntu font-medium">Encrypted Data</p>
              <p className="text-[#52525B] font-ubuntu text-sm">Bank-level encryption for all data</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CreditCard className="w-5 h-5 text-[#7a6200] mt-0.5" />
            <div>
              <p className="text-white font-ubuntu font-medium">Secure Payments</p>
              <p className="text-[#52525B] font-ubuntu text-sm">PCI-compliant processing</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Bell className="w-5 h-5 text-[#7a6200] mt-0.5" />
            <div>
              <p className="text-white font-ubuntu font-medium">Zero Tolerance</p>
              <p className="text-[#52525B] font-ubuntu text-sm">Swift action against violations</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipBenefitsSection;
